import React from 'react';
import classNames from 'classnames';

import { useRouteConfiguration } from '../../../context/routeConfigurationContext';
import { apiBaseUrl } from '../../../util/api';
import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { pathByRouteName } from '../../../util/routes';
import { navigateTo } from '../../../util/navigation';
import { SocialLoginButton } from '../../../components';

import { FacebookLogo, GoogleLogo, AppleLogo } from './socialLoginLogos';

import css from './SocialLoginButtons.module.css';

/**
 * Which identity providers are configured for this deployment.
 *
 * The `process.env.REACT_APP_*` reads have to stay as literal member expressions — CRA's build
 * replaces them textually, so a dynamic `process.env[key]` lookup would evaluate to undefined in a
 * production bundle. Reading them inside a function (rather than at module scope) also keeps the
 * existing tests working, which assign to `process.env` at runtime.
 *
 * @returns {{showFacebookLogin: boolean, showGoogleLogin: boolean, showAppleLogin: boolean}}
 */
export const socialLoginProviderFlags = () => ({
  showFacebookLogin: !!process.env.REACT_APP_FACEBOOK_APP_ID,
  showGoogleLogin: !!process.env.REACT_APP_GOOGLE_CLIENT_ID,
  showAppleLogin: !!process.env.REACT_APP_APPLE_CLIENT_ID,
});

/**
 * Facebook / Google / Apple sign-in buttons.
 *
 * Lives in its own module rather than inside AuthenticationPage.js because the Topbar's login
 * dialog renders it too: importing it from AuthenticationPage.js would make TopbarContainer depend
 * on AuthenticationPage, which imports TopbarContainer back (a cycle), and would pull the signup
 * form, the ToS page and the privacy page into the Topbar chunk that every single page loads.
 *
 * Each button is a plain full page navigation to `/api/auth/<idp>` — OAuth cannot happen inside a
 * dialog. `from` is what brings the visitor back afterwards; without it the server falls back to
 * `defaultReturn` (the landing page).
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.isLogin - Whether to phrase the buttons as log-in or as sign-up
 * @param {boolean} props.showFacebookLogin - Whether Facebook is configured
 * @param {boolean} props.showGoogleLogin - Whether Google is configured
 * @param {boolean} props.showAppleLogin - Whether Apple is configured
 * @param {string?} props.from - Path the visitor is returned to after authentication
 * @param {string?} props.userType - User type preselected before the visit to the IdP
 * @param {string?} props.className - Extra class for the wrapper, for restyling from the outside
 * @returns {JSX.Element|null}
 */
export const SocialLoginButtonsMaybe = props => {
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const {
    isLogin,
    showFacebookLogin,
    showGoogleLogin,
    showAppleLogin,
    from,
    userType,
    className,
  } = props;
  const showSocialLogins = showFacebookLogin || showGoogleLogin || showAppleLogin;

  const getDataForSSORoutes = () => {
    const baseUrl = apiBaseUrl();

    // Default route where user is returned after successfull authentication
    const defaultReturn = pathByRouteName('LandingPage', routeConfiguration);

    // Route for confirming user data before creating a new user
    const defaultConfirm = pathByRouteName('ConfirmPage', routeConfiguration);

    const queryParams = new URLSearchParams({
      ...(defaultReturn ? { defaultReturn } : {}),
      ...(defaultConfirm ? { defaultConfirm } : {}),
      // Route where the user should be returned after authentication
      // This is used e.g. with EditListingPage and ListingPage
      ...(from ? { from } : {}),
      // The preselected userType needs to be saved over the visit to identity provider's service
      ...(userType ? { userType } : {}),
    });

    return { baseUrl, queryParams: queryParams.toString() };
  };

  const authWithFacebook = () => {
    const { baseUrl, queryParams } = getDataForSSORoutes();
    navigateTo(`${baseUrl}/api/auth/facebook?${queryParams}`);
  };

  const authWithGoogle = () => {
    const { baseUrl, queryParams } = getDataForSSORoutes();
    navigateTo(`${baseUrl}/api/auth/google?${queryParams}`);
  };

  const authWithApple = () => {
    const { baseUrl, queryParams } = getDataForSSORoutes();
    navigateTo(`${baseUrl}/api/auth/apple?${queryParams}`);
  };

  const facebookAuthenticationMessage = isLogin
    ? intl.formatMessage({ id: 'AuthenticationPage.loginWithFacebook' })
    : intl.formatMessage({ id: 'AuthenticationPage.signupWithFacebook' });

  const googleAuthenticationMessage = isLogin
    ? intl.formatMessage({ id: 'AuthenticationPage.loginWithGoogle' })
    : intl.formatMessage({ id: 'AuthenticationPage.signupWithGoogle' });

  const appleAuthenticationMessage = isLogin
    ? intl.formatMessage({ id: 'AuthenticationPage.loginWithApple' })
    : intl.formatMessage({ id: 'AuthenticationPage.signupWithApple' });

  return showSocialLogins ? (
    <div className={classNames(css.idpButtons, className)}>
      <div className={css.socialButtonsOr}>
        <span className={css.socialButtonsOrText}>
          <FormattedMessage id="AuthenticationPage.or" />
        </span>
      </div>

      {/* data-idp lets a stylesheet address one provider without knowing the DOM order. Apple's
          HIG puts requirements on the Apple button that don't apply to the others (its title must
          be pure black when the logo is), and guessing at :nth-child would break the moment a
          provider is switched off by env. */}
      {showFacebookLogin ? (
        <div className={css.socialButtonWrapper} data-idp="facebook">
          <SocialLoginButton onClick={() => authWithFacebook()}>
            <span className={css.buttonIcon}>
              <FacebookLogo ariaLabelledBy="facebook-authentication-msg" />
            </span>
            <span id="facebook-authentication-msg">{facebookAuthenticationMessage}</span>
          </SocialLoginButton>
        </div>
      ) : null}

      {showGoogleLogin ? (
        <div className={css.socialButtonWrapper} data-idp="google">
          <SocialLoginButton onClick={() => authWithGoogle()}>
            <span className={css.buttonIcon}>
              <GoogleLogo ariaLabelledBy="google-authentication-msg" />
            </span>
            <span id="google-authentication-msg">{googleAuthenticationMessage}</span>
          </SocialLoginButton>
        </div>
      ) : null}

      {showAppleLogin ? (
        <div className={css.socialButtonWrapper} data-idp="apple">
          <SocialLoginButton onClick={() => authWithApple()}>
            <span className={css.buttonIcon}>
              <AppleLogo ariaLabelledBy="apple-authentication-msg" />
            </span>
            <span id="apple-authentication-msg">{appleAuthenticationMessage}</span>
          </SocialLoginButton>
        </div>
      ) : null}
    </div>
  ) : null;
};

export default SocialLoginButtonsMaybe;
